import React, { useState, useContext, useEffect, useCallback } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import { Heart, BookOpen, Users, Sparkles, Save, TrendingUp } from 'lucide-react';

const WeeklyCheckin = () => {
  const { API } = useContext(AuthContext);
  
  // Get Monday of current week
  const getMonday = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(today.setDate(diff));
    return monday.toISOString().split('T')[0];
  };

  const [weekStart, setWeekStart] = useState(getMonday());
  const [ratings, setRatings] = useState({
    pursue_god_rating: 5,
    engage_scripture_rating: 5,
    serve_others_rating: 5,
    radiate_jesus_rating: 5
  });
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchExistingCheckin = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/checkin/latest`);
      if (response.data && response.data.week_start_date === weekStart) {
        setRatings({
          pursue_god_rating: response.data.pursue_god_rating,
          engage_scripture_rating: response.data.engage_scripture_rating,
          serve_others_rating: response.data.serve_others_rating,
          radiate_jesus_rating: response.data.radiate_jesus_rating
        });
        setNotes(response.data.notes || '');
      }
    } catch (error) {
      // 404 is expected when no existing check-in; only log unexpected errors
      if (error?.response?.status !== 404) {
        console.error('WeeklyCheckin: failed to fetch latest check-in', error);
      }
    } finally {
      setLoading(false);
    }
  }, [API, weekStart]);

  useEffect(() => {
    fetchExistingCheckin();
  }, [fetchExistingCheckin]);

  const vitalSigns = [
    {
      key: 'pursue_god_rating',
      title: 'Pursue God',
      icon: Heart,
      color: '#2B6B7F',
      description: 'How consistent has your prayer life been this week?'
    },
    {
      key: 'engage_scripture_rating',
      title: 'Engage Scripture',
      icon: BookOpen,
      color: '#5FA8C1',
      description: 'How actively have you engaged with God\'s Word?'
    },
    {
      key: 'serve_others_rating',
      title: 'Serve Others',
      icon: Users,
      color: '#7BA888',
      description: 'How have you served others with your gifts?'
    },
    {
      key: 'radiate_jesus_rating',
      title: 'Radiate Jesus',
      icon: Sparkles,
      color: '#D4A574',
      description: 'How have you shared Christ with others?'
    }
  ];

  const handleRatingChange = (key, value) => {
    setRatings({ ...ratings, [key]: value[0] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.post(`${API}/checkin`, {
        week_start_date: weekStart,
        ...ratings,
        notes: notes || null
      });
      
      toast.success('Weekly check-in saved!');
    } catch (error) {
      toast.error('Failed to save check-in');
    } finally {
      setSaving(false);
    }
  };

  const getWeekDisplay = () => {
    const monday = new Date(weekStart);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    
    return `${monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${sunday.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
        <Navigation />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="text-lg text-slate-600">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50" data-testid="checkin-page">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8 animate-fade-in">
          <h1 className="text-4xl font-heading font-bold text-[#2B6B7F] mb-2">
            Weekly Check-In
          </h1>
          <p className="text-slate-600">Rate your spiritual vital signs for this week</p>
          <p className="text-sm text-slate-500 mt-2">
            Week of {getWeekDisplay()}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Ratings */}
          <Card data-testid="ratings-card">
            <CardHeader>
              <CardTitle className="text-2xl font-heading text-[#2B6B7F] flex items-center gap-2">
                <TrendingUp className="w-6 h-6" />
                Rate Your Week
              </CardTitle>
              <CardDescription>
                Use the sliders to rate each vital sign from 1 (needs improvement) to 10 (excellent)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              {vitalSigns.map((sign) => {
                const Icon = sign.icon;
                const currentRating = ratings[sign.key];
                
                return (
                  <div key={sign.key} className="space-y-3" data-testid={`rating-${sign.key}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center"
                           style={{ backgroundColor: `${sign.color}20` }}>
                        <Icon className="w-5 h-5" style={{ color: sign.color }} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-semibold text-slate-800">{sign.title}</h3>
                          <span className="text-2xl font-bold" style={{ color: sign.color }}>
                            {currentRating}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600">{sign.description}</p>
                      </div>
                    </div>
                    <Slider
                      value={[currentRating]}
                      onValueChange={(value) => handleRatingChange(sign.key, value)}
                      min={1}
                      max={10}
                      step={1}
                      className="w-full"
                      data-testid={`slider-${sign.key}`}
                    />
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>1 - Needs Improvement</span>
                      <span>10 - Excellent</span>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card data-testid="notes-card">
            <CardHeader>
              <CardTitle className="text-xl font-heading text-[#2B6B7F]">
                Additional Notes (Optional)
              </CardTitle>
              <CardDescription>
                Share any insights, challenges, or wins from this week
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="What has God been teaching you this week? Any challenges or breakthroughs?"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="min-h-[150px] resize-none"
                data-testid="notes-textarea"
              />
            </CardContent>
          </Card>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={saving}
            className="w-full h-14 text-lg bg-[#2B6B7F] hover:bg-[#1E4D5F]"
            data-testid="submit-checkin-button"
          >
            <Save className="w-5 h-5 mr-2" />
            {saving ? 'Saving...' : 'Save Weekly Check-In'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default WeeklyCheckin;
